import { useState, useCallback } from 'react';
import { LocationDTO } from '@/shared/api/model';
import { useTheme } from '@/components/common/ThemeProvider';
import { showErrorNotification } from '@/components/common/utils/notify';

// Import Orval generated API client
import {
  useSimulateLocation,
  simulateLocation as simulateLocationApi
} from '@/shared/api/client/location-management';

interface SimulatedLocation {
  zone?: LocationDTO;
  region?: LocationDTO;
  district?: LocationDTO;
  ward?: LocationDTO;
  street?: LocationDTO;
  gps?: {
    latitude: number;
    longitude: number;
  };
}

export const useLocationSimulator = () => {
  const { dark } = useTheme();
  const [simulatedLocation, setSimulatedLocation] = useState<SimulatedLocation | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use the mutation hook for simulating location
  const { 
    mutateAsync: simulateLocationMutation,
    isPending: isLoading
  } = useSimulateLocation({
    mutation: {
      onError: (error) => {
        console.error('Error simulating location:', error);
        const errorMsg = 'Failed to simulate location data';
        setError(errorMsg);
        showErrorNotification('Error', errorMsg, dark);
      }
    }
  });
  
  const simulateLocation = useCallback(async (configName: string) => {
    setError(null);
    
    try {
      const response = await simulateLocationMutation({
        params: { configName }
      });
      
      const location = (response.data ?? response) as unknown as SimulatedLocation;
      setSimulatedLocation(location);
      return location;
    } catch (err) {
      // Error is already handled by the mutation onError callback
      return null;
    }
  }, [simulateLocationMutation]);

  // Direct API call method (alternative to the mutation hook)
  const simulateLocationDirect = useCallback(async (configName: string) => {
    setError(null);
    
    try {
      const response = await simulateLocationApi({
        configName
      });
      
      const location = (response.data ?? response) as unknown as SimulatedLocation;
      setSimulatedLocation(location);
      return location;
    } catch (err) {
      console.error('Error simulating location:', err);
      const errorMsg = 'Failed to simulate location data';
      setError(errorMsg);
      showErrorNotification('Error', errorMsg, dark);
      return null;
    }
  }, [dark]);

  return {
    simulatedLocation,
    isLoading,
    error,
    simulateLocation,
    simulateLocationDirect,
    setSimulatedLocation
  };
};

export default useLocationSimulator;
